/**
 * useDetectionBridge — Ref-based bridge between VideoPanel and FSM.
 *
 * Solves the stale closure problem: VideoPanel's poseModel.onResults()
 * registers once and captures variables via closure. By using a mutable
 * ref object, both sides always read/write current values at invocation
 * time — no stale closures.
 *
 * VideoPanel writes:
 *   - lastDetection: { isGoodLight, isReadyForCalibration, isCorrectOrientation,
 *                      isMultiPerson, personInBox, isColorRedForFullBox }
 *   - detectionVersion: incremented on each write
 *
 * MovementCore writes:
 *   - sendEvent: fsm.send (kept current)
 *   - isModalOpen: derived from FSM + other modal flags
 *   - isMovementPaused: from pause ref
 *   - selectedExercise: current exercise
 *   - isExamineticsEnabled: tenant flag
 *   - acceptedStates: which FSM states accept detection input
 *
 * MovementCore reads (via useEffect):
 *   - Processes detection snapshots into REQUIREMENTS_MET / REQUIREMENTS_LOST
 */
import { useRef, useEffect } from 'react';
import { MovementState, MovementEvent } from './movementFSMConstants';
import CONSTANT from '../../../../constants/Constant';

export default function useDetectionBridge(fsm) {
  const bridgeRef = useRef({
    // ── Written by VideoPanel ──
    lastDetection: null,
    detectionVersion: 0,

    // ── Written by MovementCore ──
    sendEvent: fsm.send,
    isModalOpen: false,
    isMovementPaused: false,
    selectedExercise: null,
    isExamineticsEnabled: false,
    fsmState: fsm.currentState,
  });

  // Keep MovementCore-side values current (sync, no stale reads)
  bridgeRef.current.sendEvent = fsm.send;
  bridgeRef.current.fsmState = fsm.currentState;

  // States that accept detection input
  const ACCEPTED_STATES = [
    MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    MovementState.WAITING_FOR_USER_VALIDATION,
    MovementState.USER_READY_FOR_PRE_RECORDING,
    MovementState.RUNNING_PRE_RECORDING_COUNTDOWN,
  ];

  // Process detection snapshots into FSM events
  const lastProcessedVersion = useRef(0);

  useEffect(() => {
    const bridge = bridgeRef.current;
    const snapshot = bridge.lastDetection;
    if (!snapshot) return;
    if (bridge.detectionVersion === lastProcessedVersion.current) return;
    lastProcessedVersion.current = bridge.detectionVersion;

    // Gate: only process in accepted states
    if (!ACCEPTED_STATES.includes(bridge.fsmState)) return;
    if (bridge.isModalOpen) return;
    if (bridge.isMovementPaused) return;

    // SHOWING_MOVEMENT_INSTRUCTION: only detect person-in-box (non-Examinetics)
    if (bridge.fsmState === MovementState.SHOWING_MOVEMENT_INSTRUCTION) {
      if (!bridge.isExamineticsEnabled && snapshot.personInBox && !snapshot.isColorRedForFullBox) {
        console.log('[BRIDGE] person detected in box during SHOWING_MOVEMENT_INSTRUCTION');
        bridge.sendEvent(MovementEvent.PERSON_DETECTED_IN_BOX);
      }
      return;
    }

    // Evaluate requirements for remaining states
    const exerciseCodeName = bridge.selectedExercise?.ExerciseCodeName;
    const needsOrientation = exerciseCodeName !== CONSTANT.EXERCISES_NAME.calibration
      && exerciseCodeName !== CONSTANT.EXERCISES_NAME.postureAnalysis;

    const allRequirementsMet =
      snapshot.isReadyForCalibration
      && snapshot.personInBox
      && !snapshot.isColorRedForFullBox
      && (!needsOrientation || snapshot.isCorrectOrientation);

    if (allRequirementsMet) {
      if (bridge.fsmState === MovementState.WAITING_FOR_USER_VALIDATION) {
        bridge.sendEvent(MovementEvent.REQUIREMENTS_MET);
      }
    } else {
      if (bridge.fsmState !== MovementState.WAITING_FOR_USER_VALIDATION) {
        bridge.sendEvent(MovementEvent.REQUIREMENTS_LOST);
      }
    }
  }); // Run after every render

  return { ref: bridgeRef };
}
